import {
  ChatConversationMessageInput,
  RequestClassInquiryMutation,
  Role
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import React, { useCallback } from "react";

import { ACTION_TYPES, chatReducer } from "./chatReducer";
import { ErrorMessage } from "./components/ErrorMessage";
import { SelectedLearner } from "./components/LearnerSelection";
import { Message as MessageComponent } from "./components/Message";
import {
  ClassInquiryStatus,
  GUIDE_V3_FEATURE_FLAG,
  MESSAGE_ID,
  Message,
  MessageIdType,
  Sender
} from "./consts";
import { ParentMessageWithLearnerPill } from "./teacherPullChat/ParentResponseClassRequestMessage";
import { SendClassRecommendationMessage } from "./teacherPullChat/SendClassRecommendationMessage";
import { SendClassRequestMessage } from "./teacherPullChat/SendClassRequestMessage";
import { Welcome } from "./Welcome";

export type ClassInquiryType = NonNullable<
  RequestClassInquiryMutation["requestClassInquiry"]
>;

type topRecommendedActivityListingType =
  | ClassInquiryType["topRecommendedActivityListing"]
  | undefined;

export type StateType = {
  currentMessageId: MessageIdType;
  conversation: Message[];
  classInquiry?: ClassInquiryType;
  selectedLearner?: SelectedLearner;
};

export type ChatContextType = StateType & {
  sendClassInquirySubmit: (_: string) => void;
  classInquiryCreated: (_: ClassInquiryType) => void;
  getFollowUpConversation: () => ChatConversationMessageInput[];
  updateMessages: (_: string) => void;
  classInquiryUpdated: (
    _recommendedClass: topRecommendedActivityListingType | null,
    _searchPath: string | null,
    _followUpQuestion: string | null,
    _nonValidReason: string | null
  ) => void;
  findANewClassOnClick: () => void;
  helpMeWithACurrentClassOnClick: () => void;
  sendAiGuideSupportIssueSubmit: (_: string) => void;
  showErrorMessage: (_a: string, _b: string) => void;
  updateSelectedLearner: (_: SelectedLearner) => void;
  setIsModalOpen?: (_: boolean) => void;
};

const CONTEXT_DEFAULT_VALUE: ChatContextType = {
  currentMessageId: MESSAGE_ID.WELCOME,
  conversation: [],
  sendClassInquirySubmit: () => {},
  classInquiryCreated: () => {},
  getFollowUpConversation: () => [],
  updateMessages: () => {},
  classInquiryUpdated: () => {},
  findANewClassOnClick: () => {},
  helpMeWithACurrentClassOnClick: () => {},
  sendAiGuideSupportIssueSubmit: () => {},
  updateSelectedLearner: () => {},
  showErrorMessage: () => {}
};

export const INIT_STATE: StateType = {
  currentMessageId: MESSAGE_ID.WELCOME,
  conversation: []
};

const ChatContext = React.createContext<ChatContextType>(CONTEXT_DEFAULT_VALUE);

type ChatProviderProps = {
  setIsModalOpen?: (_: boolean) => void;
  children?: React.ReactNode;
  initState?: StateType;
};

const canRecommendAnActivity = (classInquiryStatus: string) =>
  classInquiryStatus === ClassInquiryStatus.Eligible ||
  classInquiryStatus === ClassInquiryStatus.EligibleButNotShared;

export const ChatProvider: React.FC<
  React.PropsWithChildren<ChatProviderProps>
> = ({ setIsModalOpen, children, initState = INIT_STATE }) => {
  const { t } = useTranslation("client\\components\\chat");
  const trackEvent = useTrackEvent();
  const isGuideV3 = useFeatureFlag(GUIDE_V3_FEATURE_FLAG);

  const [
    { currentMessageId, conversation, classInquiry, selectedLearner },
    dispatch
  ] = React.useReducer(chatReducer, initState);

  const classInquiryUid = classInquiry?.classInquiries[0]?.uid;

  const getFollowUpConversation = useCallback(() => {
    return conversation
      .filter(
        message =>
          message.mainText &&
          message.referencesClassInquiryUid === classInquiryUid
      )
      .map(
        message =>
          ({
            role: String(message.sender) as Role,
            content: message.mainText
          } as ChatConversationMessageInput)
      );
  }, [classInquiryUid, conversation]);

  const updateSelectedLearner = (learner: SelectedLearner) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_LEARNER,
      payload: {
        learner
      }
    });
  };

  const classInquiryCreated = (classInquiry: ClassInquiryType) => {
    if (isGuideV3) {
      const messages = [];
      if (classInquiry.topRecommendedActivityListing) {
        messages.push({
          sender: Sender.System,
          render: () => (
            <SendClassRecommendationMessage
              recommendedClass={classInquiry.topRecommendedActivityListing}
              searchPath={classInquiry.searchPath}
            />
          )
        });
      }

      if (
        !classInquiry.topRecommendedActivityListing &&
        classInquiry.followUpQuestion
      ) {
        messages.push({
          sender: Sender.Assistant,
          render: () => (
            <MessageComponent
              text={classInquiry.followUpQuestion || ""}
              sender={Sender.System}
            />
          ),
          mainText: classInquiry.followUpQuestion || "",
          referencesClassInquiryUid: classInquiry.classInquiries[0]?.uid
        });

        trackEvent("teacher_pull_chat_class_inquiry_no_recommendation");
      }

      if (classInquiry.nonValidReason) {
        messages.push({
          sender: Sender.System,
          render: () => (
            <ErrorMessage text={classInquiry.nonValidReason as string} />
          )
        });
      }

      dispatch({
        type: ACTION_TYPES.CLASS_INQUIRY_CREATED,
        payload: {
          nextMessageId: classInquiry.nonValidReason
            ? MESSAGE_ID.RETRY_SEND_CLASS_REQUEST
            : MESSAGE_ID.FOLLOWUP_QUESTION,
          classInquiry,
          messages
        }
      });

      trackEvent("teacher_pull_chat_class_inquiry_created", {
        classInquiryId: classInquiry?.classInquiries[0]?.uid,
        followUpQuestion: classInquiry?.followUpQuestion,
        nonValidReason: classInquiry?.nonValidReason
      });
      return;
    }

    const canRecommend =
      classInquiry.classInquiries[0]?.status &&
      canRecommendAnActivity(classInquiry.classInquiries[0]?.status);
    const messages = [];
    if (canRecommend && classInquiry.topRecommendedActivityListing) {
      messages.push({
        sender: Sender.System,
        render: () => (
          <SendClassRecommendationMessage
            recommendedClass={classInquiry.topRecommendedActivityListing}
            searchPath={classInquiry.searchPath}
          />
        )
      });

      if (classInquiry.followUpQuestion) {
        messages.push({
          sender: Sender.Assistant,
          render: () => (
            <MessageComponent
              text={classInquiry.followUpQuestion || ""}
              sender={Sender.System}
            />
          )
        });
      }
      dispatch({
        type: ACTION_TYPES.CLASS_INQUIRY_CREATED,
        payload: {
          nextMessageId: MESSAGE_ID.FOLLOWUP_QUESTION,
          classInquiry,
          messages
        }
      });
      trackEvent("teacher_pull_chat_class_inquiry_created", {
        classInquiryId: classInquiry?.classInquiries[0]?.uid,
        followUpQuestion: classInquiry?.followUpQuestion
      });
      return;
    }

    // Error case
    dispatch({
      type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
      payload: {
        nextMessageId: MESSAGE_ID.RETRY_SEND_CLASS_REQUEST,
        messages: [
          {
            sender: Sender.System,
            render: () => (
              <ErrorMessage
                text={t(
                  "It looks like we need a little more information to help you. Please add any additional details and try again."
                )}
              />
            )
          }
        ]
      }
    });

    trackEvent("teacher_pull_chat_class_inquiry_no_recommendation");
    trackEvent("teacher_pull_chat_class_inquiry_created", {
      classInquiryId: classInquiry?.classInquiries[0]?.uid,
      followUpQuestion: classInquiry?.followUpQuestion
    });
  };

  const findANewClassOnClick = () => {
    if (currentMessageId === MESSAGE_ID.WELCOME) {
      dispatch({
        type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
        payload: {
          nextMessageId: MESSAGE_ID.SEND_CLASS_REQUEST,
          messages: [
            {
              sender: Sender.System,
              render: () => <Welcome />
            },
            {
              sender: Sender.User,
              render: () => (
                <MessageComponent
                  text={t`Help me find a new class`}
                  sender={Sender.User}
                />
              )
            }
          ]
        }
      });
    } else {
      dispatch({
        type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
        payload: {
          nextMessageId: MESSAGE_ID.SEND_CLASS_REQUEST,
          messages: [
            {
              sender: Sender.User,
              render: () => (
                <MessageComponent
                  text={t`Help me find a new class`}
                  sender={Sender.User}
                />
              )
            }
          ]
        }
      });
    }

    trackEvent("ai_guide_find_a_new_class_button_clicked");
  };

  const helpMeWithACurrentClassOnClick = () => {
    dispatch({
      type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
      payload: {
        nextMessageId: MESSAGE_ID.HELP_ME_WITH_CURRENT_CLASS,
        messages: [
          {
            sender: Sender.User,
            render: () => <Welcome />
          },
          {
            sender: Sender.System,
            render: () => (
              <MessageComponent
                text={t`Help me with a current class`}
                sender={Sender.User}
              />
            )
          }
        ]
      }
    });
    trackEvent("ai_guide_help_with_current_class_button_clicked");
  };

  const sendClassInquirySubmit = (requestText: string) => {
    if (currentMessageId === MESSAGE_ID.RETRY_SEND_CLASS_REQUEST) {
      dispatch({
        type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
        payload: {
          nextMessageId: MESSAGE_ID.LOADING,
          messages: [
            {
              sender: Sender.User,
              render: () => (
                <ParentMessageWithLearnerPill
                  text={requestText}
                ></ParentMessageWithLearnerPill>
              )
            }
          ]
        }
      });
    } else {
      dispatch({
        type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
        payload: {
          nextMessageId: MESSAGE_ID.LOADING,
          messages: [
            {
              sender: Sender.System,
              render: () => <SendClassRequestMessage />
            },
            {
              sender: Sender.User,
              render: () => (
                <ParentMessageWithLearnerPill
                  text={requestText}
                ></ParentMessageWithLearnerPill>
              )
            }
          ]
        }
      });
    }
  };

  const updateMessages = (inputText: string) => {
    dispatch({
      type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
      payload: {
        nextMessageId: MESSAGE_ID.LOADING,
        messages: [
          {
            sender: Sender.User,
            render: () => <ParentMessageWithLearnerPill text={inputText} />,
            mainText: inputText,
            referencesClassInquiryUid: classInquiryUid
          }
        ]
      }
    });
  };

  const classInquiryUpdated = (
    recommendedClass: topRecommendedActivityListingType | null,
    searchPath: string | null,
    followUpQuestion: string | null,
    nonValidReason: string | null
  ) => {
    const messages = [];

    if (recommendedClass && searchPath) {
      messages.push({
        sender: Sender.System,
        render: () => (
          <SendClassRecommendationMessage
            recommendedClass={recommendedClass}
            searchPath={searchPath}
          />
        )
      });
    }

    if (followUpQuestion && (!isGuideV3 || (isGuideV3 && !recommendedClass))) {
      messages.push({
        sender: Sender.Assistant,
        render: () => (
          <MessageComponent text={followUpQuestion} sender={Sender.System} />
        ),
        mainText: followUpQuestion,
        referencesClassInquiryUid: classInquiryUid
      });
    }

    if (!recommendedClass && !followUpQuestion) {
      messages.push({
        sender: Sender.System,
        render: () => (
          <ErrorMessage
            text={t(
              "It looks like we need a little more information to help you. Please add any additional details and try again."
            )}
          />
        )
      });

      if (nonValidReason) {
        messages.push({
          sender: Sender.System,
          render: () => <ErrorMessage text={nonValidReason} />
        });
      }

      trackEvent("ai_guide_class_inquiry_follow_up_no_recommendation");
    }

    dispatch({
      type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
      payload: {
        nextMessageId: MESSAGE_ID.FOLLOWUP_QUESTION,
        messages
      }
    });
  };

  const sendAiGuideSupportIssueSubmit = (userMessage: string) => {
    dispatch({
      type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
      payload: {
        nextMessageId: MESSAGE_ID.SEND_SUPPORT_ISSUE,
        messages: [
          {
            sender: Sender.User,
            render: () => (
              <MessageComponent text={userMessage} sender={Sender.User} />
            )
          },
          {
            sender: Sender.System,
            render: () => (
              <MessageComponent
                text={t`We got your message! Our support team may contact you to help resolve this issue.`}
                sender={Sender.System}
              />
            )
          }
        ]
      }
    });
  };

  const showErrorMessage = (userMessage: string, errorMessage: string) => {
    dispatch({
      type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
      payload: {
        nextMessageId: MESSAGE_ID.WELCOME_ACTIONS,
        messages: [
          {
            sender: Sender.User,
            render: () => (
              <MessageComponent text={userMessage} sender={Sender.User} />
            )
          },
          {
            sender: Sender.System,
            render: () => <ErrorMessage text={errorMessage} />
          }
        ]
      }
    });
  };

  return (
    <ChatContext.Provider
      value={{
        currentMessageId,
        conversation,
        sendClassInquirySubmit,
        classInquiryCreated,
        getFollowUpConversation,
        updateMessages,
        classInquiryUpdated,
        classInquiry,
        showErrorMessage,
        setIsModalOpen,
        helpMeWithACurrentClassOnClick,
        sendAiGuideSupportIssueSubmit,
        findANewClassOnClick,
        updateSelectedLearner,
        selectedLearner
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  return React.useContext(ChatContext);
};
